// src/components/AdminLoaRequests.tsx
//
// Admin tool (#687): the campaign asking clients who have not signed an LOA to
// sign one. Shows who is asked (in plain words), today's sending against the
// daily limit, progress, and the daily sending; Check is a dry run of the next
// clients (nothing sent); "Send a test batch" sends a small confirmed batch.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LoaBacklogResult,
  LoaCampaignStatus,
  checkLoaRequests,
  getLoaCampaign,
  resumeLoaRunner,
  sendLoaTestBatch,
} from '../api/loaRequests';
import { Button, Card, PageHeader, StatCard } from './ui';

export const testBatchConfirmText = (n: number) =>
  `Send LOA requests to ${n} clients? Each client gets an email asking them to sign. ` +
  'No letters go to lenders until they sign.';

const number = (n: number | null | undefined) => (n === null || n === undefined ? '—' : n.toLocaleString('en-GB'));

const when = (iso: string | null | undefined) => {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const plainStop = (stopped: string | null | undefined): string | null => {
  if (!stopped || stopped === 'Most sends for one call reached') return null;
  if (stopped === 'Daily cap reached') return 'The daily limit has been reached.';
  return stopped;
};

const errorText = (err: any, fallback: string): string =>
  err?.response?.data?.error || err?.response?.data?.message || fallback;

/** Skipped rows grouped by their plain reason, largest first. */
export const skippedByReason = (rows: LoaBacklogResult['results']): Array<[string, number]> => {
  const counts = new Map<string, number>();
  rows
    .filter((row) => row.outcome !== 'would-send' && row.outcome !== 'sent')
    .forEach((row) => {
      const key = row.skipReason || row.detail || 'Not sent';
      counts.set(key, (counts.get(key) || 0) + 1);
    });
  return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
};

const ReasonList: React.FC<{ title: string; items: Array<[string, number]> }> = ({ title, items }) =>
  items.length ? (
    <div className="mt-3 text-sm">
      <div className="font-medium text-gray-700 mb-1">{title}</div>
      <ul className="text-gray-600">
        {items.map(([why, count]) => (
          <li key={why}>
            {why}: {number(count)}
          </li>
        ))}
      </ul>
    </div>
  ) : null;

const AdminLoaRequests: React.FC = () => {
  const [status, setStatus] = useState<LoaCampaignStatus | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<'checking' | 'sending' | 'resuming' | null>(null);
  const [check, setCheck] = useState<LoaBacklogResult | null>(null);
  const [sendNote, setSendNote] = useState<{ text: string; ok: boolean } | null>(null);

  const load = useCallback(
    () =>
      getLoaCampaign()
        .then((data) => setStatus(data))
        .catch((err) => setError(errorText(err, 'Could not load the LOA request campaign'))),
    [],
  );

  useEffect(() => {
    load();
  }, [load]);

  const checkSummary = useMemo(() => {
    if (!check) return null;
    const asked = check.results.filter((row) => row.outcome === 'would-send');
    return {
      checked: check.results.length,
      asked: asked.length,
      claims: asked.reduce((sum, row) => sum + (row.linkClaimRefs?.length || 0), 0),
      skipped: skippedByReason(check.results),
    };
  }, [check]);

  if (!status) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <PageHeader title="LOA requests" />
        {error ? (
          <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        ) : (
          <p className="text-sm text-gray-500">Loading…</p>
        )}
      </div>
    );
  }

  const settings = status.campaign.settings;
  const ready = status.campaign.ready && !!settings;
  const batchSize = status.limits.testBatchSize;
  const lastSkipped: Array<[string, number]> = status.lastRun.skipped.map((s) => [s.reason, s.count]);

  const handleCheck = async () => {
    if (!settings) return;
    setBusy('checking');
    setError('');
    setSendNote(null);
    try {
      setCheck(await checkLoaRequests(settings, status.cursor, status.limits.checkSize));
    } catch (err: any) {
      setError(errorText(err, 'Check failed'));
    } finally {
      setBusy(null);
    }
  };

  const handleSend = async () => {
    if (!settings || !status.sendEnabled) return;
    if (!window.confirm(testBatchConfirmText(batchSize))) return;
    setBusy('sending');
    setError('');
    setSendNote(null);
    try {
      const data = await sendLoaTestBatch(settings, status.cursor, batchSize);
      const sent = data.results.filter((row) => row.outcome === 'sent').length;
      const problems = data.results.filter((row) => ['failed', 'error', 'not-sent'].includes(row.outcome)).length;
      const stop = plainStop(data.stopped);
      setSendNote({
        text: `${number(sent)} LOA request${sent === 1 ? '' : 's'} sent. ${number(problems)} not sent.${stop ? ` ${stop}` : ''}`,
        ok: problems === 0 && !(data.stopped || '').startsWith('Stopped'),
      });
      setCheck(null);
      await load();
    } catch (err: any) {
      setError(errorText(err, 'Sending failed. Press Check before trying again: it shows what went.'));
    } finally {
      setBusy(null);
    }
  };

  const handleResume = async () => {
    setBusy('resuming');
    setError('');
    try {
      await resumeLoaRunner();
      await load();
    } catch (err: any) {
      setError(errorText(err, 'Could not restart the daily sending'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageHeader
        title="LOA requests"
        description="Asks clients who have not signed an LOA to sign one, a few each day. When a client signs, their letter of claim goes to the lender with the new LOA in the usual way."
      />

      {error && (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}

      {!ready && (
        <div role="alert" className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg mb-4 text-sm">
          <div className="font-medium">The campaign is not set up yet, so nothing can be checked or sent.</div>
          {status.campaign.notChosen.length > 0 && (
            <>
              <div className="mt-1">Still to be chosen:</div>
              <ul className="list-disc ml-5">
                {status.campaign.notChosen.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {status.halted && (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
          <div>
            Daily sending stopped itself on {when(status.halted.at)}: {status.halted.reason}
          </div>
          <div className="mt-2 flex items-center gap-3">
            <span>Check what happened, then let it carry on.</span>
            <Button variant="secondary" size="sm" onClick={handleResume} disabled={!!busy}>
              Carry on
            </Button>
          </div>
        </div>
      )}

      {ready && (
        <Card className="mb-4">
          <div className="font-medium text-gray-700 mb-1 text-sm">Who is asked</div>
          <ul className="list-disc ml-5 text-sm text-gray-600">
            {status.campaign.plain.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard value={`${number(status.sentToday)} of ${number(status.dailyCap)}`} label="Sent today" />
        <StatCard value={number(status.totalSent)} label="Sent so far" tone="success" />
        <StatCard value={number(status.leftToCheck)} label="Left to check" tone="muted" />
        <StatCard
          value={status.runner.enabled ? 'On' : 'Off'}
          label="Daily sending"
          tone={status.runner.enabled ? 'success' : 'muted'}
        />
      </div>

      <Card className="mb-4">
        <p className="text-sm text-gray-700">
          {status.runner.enabled
            ? `Daily sending is on: every ${status.runner.intervalMinutes} minutes ${
                status.runner.hours ? `from ${status.runner.hours} (UK time)` : ''
              }, up to ${number(status.dailyCap)} a day.`
            : 'Daily sending is off: nothing is sent automatically. It is turned on in the app’s settings.'}
        </p>
        <p className="text-sm text-gray-600 mt-1">
          {status.lastRun.at
            ? `Last run: ${when(status.lastRun.at)}${status.lastRun.outcome ? ` – ${status.lastRun.outcome}` : ''}`
            : 'Last run: not yet'}
        </p>
        <ReasonList title="Skipped at the last run" items={lastSkipped} />
      </Card>

      <Card className="mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="secondary" onClick={handleCheck} disabled={!ready || !!busy}>
            Check
          </Button>
          <Button variant="primary" onClick={handleSend} disabled={!ready || !status.sendEnabled || !!busy}>
            Send a test batch ({batchSize})
          </Button>
          {busy === 'checking' && <span className="text-sm text-gray-500">Checking the next {status.limits.checkSize} clients…</span>}
          {busy === 'sending' && <span className="text-sm text-gray-500">Sending, a second apart…</span>}
        </div>
        <p className="mt-2 text-sm text-gray-600">
          Check looks at the next {status.limits.checkSize} clients and sends nothing.
        </p>
        {!status.sendEnabled && (
          <p className="mt-1 text-sm text-gray-600" data-testid="send-off-note">
            Sending is switched off, so the test batch can’t be sent yet. It is turned on in the app’s settings.
          </p>
        )}
      </Card>

      {sendNote && (
        <p role="status" className={`mb-3 text-sm font-medium ${sendNote.ok ? 'text-green-700' : 'text-red-700'}`}>
          {sendNote.text}
        </p>
      )}

      {checkSummary && (
        <Card>
          <div className="font-medium text-gray-700 mb-2 text-sm">
            Check of the next {number(checkSummary.checked)} clients – nothing was sent
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <StatCard value={number(checkSummary.asked)} label="Would be asked" />
            <StatCard value={number(checkSummary.claims)} label="Claims they would sign for" tone="muted" />
            <StatCard value={number(checkSummary.checked - checkSummary.asked)} label="Skipped" tone="muted" />
          </div>
          <ReasonList title="Skipped because" items={checkSummary.skipped} />
          {check?.suppressionsChecked === false && (
            <p className="mt-3 text-sm text-amber-700">
              The list of email addresses that can’t receive our emails was still loading, so a few more may be
              skipped when sending.
            </p>
          )}
        </Card>
      )}

      {status.duplicates && status.duplicates.count > 0 && (
        <Card className="mt-4">
          <div className="font-medium text-gray-700 text-sm" data-testid="duplicates-heading">
            Duplicate client records to merge: {number(status.duplicates.count)}
          </div>
          <p className="text-sm text-gray-600 mt-1 mb-2">
            Each of these client records has the same email address as another record that was already asked, so
            it was not sent a second request. Merge the two records.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4 font-medium">Claims on the record not asked</th>
                  <th className="py-2 pr-4 font-medium">Record not asked</th>
                  <th className="py-2 pr-4 font-medium">Record already asked</th>
                  <th className="py-2 font-medium">Email domain</th>
                </tr>
              </thead>
              <tbody>
                {status.duplicates.items.map((item) => (
                  <tr key={item.clientId} className="border-b border-gray-100" data-testid="duplicate-row">
                    <td className="py-2 pr-4 font-mono">{item.claimRefs.join(', ') || '—'}</td>
                    <td className="py-2 pr-4 font-mono">{item.clientId}</td>
                    <td className="py-2 pr-4 font-mono">{item.askedClientId || '—'}</td>
                    <td className="py-2">{item.emailDomain || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AdminLoaRequests;
